/**
 * DirectoryWalker — recursive traversal of a folder. That is its only job.
 *
 * It reports what it finds and never decides what to do with it: no database, no
 * thumbnails, no filtering beyond "is this media m3cull handles".
 */

import { classify, extensionOf, isIgnoredName } from '../domain/media-type';
import type { FsDirectory } from './fs-port';
import type { MediaType } from '../types';

export interface WalkedFile {
  relPath: string;
  filename: string;
  type: MediaType;
  ext: string;
  sizeBytes: number;
  modifiedAt: number;
}

export interface WalkOptions {
  /** Abort as soon as this flips true — a cancelled scan must stop promptly. */
  signal?: { aborted: boolean };
  /** Called after each directory so the UI can show progress. */
  onProgress?: (found: number, skipped: number) => void;
  /** Guards against symlink loops and absurdly deep card structures. */
  maxDepth?: number;
}

export class DirectoryWalker {
  async walk(root: FsDirectory, options: WalkOptions = {}): Promise<WalkedFile[]> {
    const found: WalkedFile[] = [];
    let skipped = 0;
    const maxDepth = options.maxDepth ?? 12;

    const visit = async (dir: FsDirectory, prefix: string, depth: number): Promise<void> => {
      if (options.signal?.aborted || depth > maxDepth) return;

      const entries = await dir.list();
      // Deterministic order so two scans of the same card agree.
      entries.sort((a, b) => a.name.localeCompare(b.name));

      for (const entry of entries) {
        if (options.signal?.aborted) return;
        if (isIgnoredName(entry.name)) continue;

        const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;

        if (entry.kind === 'directory') {
          await visit(await dir.directory(entry.name), relPath, depth + 1);
          continue;
        }

        const type = classify(entry.name);
        if (!type) {
          skipped++;
          continue;
        }

        try {
          const file = await dir.file(entry.name);
          found.push({
            relPath,
            filename: entry.name,
            type,
            ext: extensionOf(entry.name),
            sizeBytes: file.size,
            modifiedAt: file.lastModified,
          });
        } catch {
          skipped++; // vanished between listing and opening
        }
      }

      options.onProgress?.(found.length, skipped);
    };

    await visit(root, '', 0);
    return found;
  }
}
